/**
 * The web activity record, browser side (job #603; plan: docs/web-activity-plan-2026-10-13.md).
 *
 * What the games and site pages outside the app send to /api/web/event: a page visit, a game
 * started, a game finished with its score, a way into a course offered and taken. First party,
 * our own endpoint; no outside tracker, no fingerprint, and the server keeps the country only.
 *
 * NO QUESTION ASKED (Tom, 2026-10-15): each event carries this page load's memory-only visit id and the
 * counting id (countingConsent.visitorId), under PECR's statistical purposes exception — the rules that
 * exception imposes are in countingConsent.ts's header. A device that objected sends nothing at all.
 */
import { inject, onMounted, type Ref } from 'vue'
import { isProbeClient } from '@/composables/probeClient'
import { countingOff, visitId, visitorId } from '@/composables/countingConsent'

export { COUNTING_OFF_KEY, VISITOR_KEY, countingOff, setCountingOff, visitId, visitorId, resetVisit } from '@/composables/countingConsent'

export type WebEventKind = 'page_view' | 'game_start' | 'game_finish' | 'signup_prompt_shown' | 'signup_prompt_taken' | 'app_open'

/** A page path without its query or hash: the route's shape, nothing typed by a person. */
export function cleanPath(p: string): string {
  return (p.split(/[?#]/)[0] || '/').slice(0, 120)
}

export interface WebEventBody {
  kind: WebEventKind
  path: string
  source?: string
  score?: number
  visitId: string
  visitorId: string | null
  probe: boolean
}

export function eventBody(kind: WebEventKind, extra: { source?: string; score?: number; path?: string } = {}): WebEventBody {
  return {
    kind,
    path: cleanPath(extra.path ?? (typeof location !== 'undefined' ? location.pathname : '/')),
    ...(extra.source ? { source: extra.source } : {}),
    ...(typeof extra.score === 'number' ? { score: extra.score } : {}),
    visitId: visitId(),
    visitorId: visitorId(),
    probe: isProbeClient(),
  }
}

/**
 * Send one event. A signed-in page sends its token so the event carries the learner; a beacon
 * (page closing) cannot, and goes without. Never throws, never waits: counting never gets in the way.
 */
export function sendWebEvent(body: WebEventBody, token: string | null = null, beacon = false): void {
  if (countingOff()) return
  try {
    const json = JSON.stringify(body)
    if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // A false result means the browser did not queue the event.
      if (navigator.sendBeacon('/api/web/event', new Blob([json], { type: 'text/plain' }))) return
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    void fetch('/api/web/event', { method: 'POST', headers, body: json, keepalive: true }).catch(() => null)
  } catch {
    /* counting never gets in the way */
  }
}

/**
 * In a page: `const web = useWebActivity()`; a page visit is sent on mount, and web.track()
 * sends the rest. `source` names the game, as gameRecording names it.
 */
export function useWebActivity(opts: { pageView?: boolean } = {}) {
  const supabase = inject<Ref<any> | null>('supabase', null)
  async function token(): Promise<string | null> {
    try {
      const r = await supabase?.value?.auth.getSession()
      return r?.data?.session?.access_token ?? null
    } catch {
      return null
    }
  }
  function track(kind: WebEventKind, extra: { source?: string; score?: number } = {}): void {
    const body = eventBody(kind, extra)
    void token().then((t) => sendWebEvent(body, t))
  }
  if (opts.pageView !== false) onMounted(() => track('page_view'))
  return { track }
}
